import type { AICandidate, AIClarification } from '../errors.js';
import { normalizeText } from './normalize.js';
import { grainOf } from './parse-user-intent.js';

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Reformula el prompt original con el candidato elegido por el usuario para resolver una
 * aclaracion. Determinista y sin interpretar texto del proveedor: solo sustituye el
 * termino ambiguo por la etiqueta del candidato. El resultado vuelve a pasar por todo el
 * pipeline (guardrails incluidos). `null` si el candidato no se resuelve reformulando
 * (filtros o visualizaciones, que la UI resuelve con comandos explicitos).
 *
 * @example
 * rephraseWithCandidate('ventas por fecha', clarification, { id: 'dim.order_date', label: 'Fecha de pedido', kind: 'dimension' });
 * // 'ventas por fecha de pedido'
 */
export function rephraseWithCandidate(
  prompt: string,
  clarification: AIClarification,
  candidate: AICandidate,
): string | null {
  if (candidate.kind === 'filter' || candidate.kind === 'visualization') return null;
  const normalized = normalizeText(prompt);
  const label = normalizeText(candidate.label);
  const term = clarification.term === undefined ? undefined : normalizeText(clarification.term);
  if (term === undefined || term === '') return `${normalized} ${label}`;
  const replacement =
    candidate.kind === 'member'
      ? `${label} ${term}`
      : candidate.kind === 'dimension' && grainOf(term) !== undefined
        ? `${label} por ${term}`
        : label;
  const pattern = new RegExp(`(^|\\s)${escapeRegExp(term)}(?=\\s|$)`);
  if (!pattern.test(normalized)) return `${normalized} ${label}`;
  return normalized.replace(pattern, (_match, lead: string) => `${lead}${replacement}`);
}
